import { resolveMatchedBranch } from './conditional-branch.js';
import { pickVersion, versionRoles, versionRollKey } from './versions.js';
/** A leader is a display block with a bank worth drawing from. `rollVersions` already ignores a
 *  bank below two, so a one-variant "bank" never varies and offering it as a leader would be a
 *  link that silently does nothing. */
export function canLeadScenarios(data) {
    return (data?.versions?.length ?? 0) >= 2;
}
/**
 * The leader's drawn variant, read out of the task roll.
 *
 * Followers deliberately do NOT have a roll entry of their own: the group draws once, under the
 * leader's key. That is what makes the whole cluster consistent by construction rather than by
 * agreement, and it is also what lets an attempt record say which scenario every answer in the
 * cluster was given under, from a single entry and with no new field.
 */
export function drawnScenarioId(followsBlockId, 
/** Nullable because a task roll is: no session, no draw. */
roll) {
    if (!followsBlockId || !roll)
        return undefined;
    return roll[versionRollKey(followsBlockId)];
}
/**
 * The branch that answers the drawn scenario, or null.
 *
 * Falls back to nothing rather than to the first branch — unlike `pickVersion`, which falls back
 * to the first version because a block must always show SOMETHING. An answer key has no such
 * obligation, and guessing one would mark a student against a scenario they were never shown.
 * The caller uses the block's own top-level answer setup instead, which is what an unlinked
 * block would have done.
 */
export function pickScenarioBranch(branches, scenarioId) {
    if (!branches || !scenarioId)
        return null;
    return branches.find(b => b.scenarioId === scenarioId) ?? null;
}
/**
 * THE PROJECTION: a follower's rows are the leader's variants, never a list the teacher maintains.
 *
 * Called whenever either side could have moved — on linking, and whenever the leader's bank
 * changes. Returns rows in the LEADER's order, so the editor shows them in the order the
 * scenarios are authored in.
 *
 *   - a row whose scenario still exists is kept as it is, including its `unreviewed` flag —
 *     unless the scenario has been RENAMED since the row was decided, which puts it back to
 *     undecided; see `ScenarioBranchBase.scenarioName`
 *   - a scenario with no row gains one from `template`, marked `unreviewed`
 *   - a row whose scenario is gone is dropped, and returned in `removed` so the caller can say so
 *
 * The teacher never adds, reorders or deletes a row. That is the point: the correspondence
 * between "scenario 2" and "the answer in scenario 2" is not stored anywhere, so it cannot drift.
 */
export function syncScenarioBranches(leaderVersions, existing, template) {
    const versions = leaderVersions ?? [];
    const have = existing ?? [];
    const byId = new Map(have.map(b => [b.scenarioId, b]));
    const branches = versions.map(v => {
        const kept = byId.get(v.id);
        if (!kept)
            return { ...template(), scenarioId: v.id, unreviewed: true, scenarioName: v.name };
        /*
         * WHILE IT IS UNDECIDED THE NAME TRACKS THE VARIANT, so the moment a teacher reviews the
         * row the recorded words are the ones they were looking at. Nothing else maintains it:
         * `markScenarioReviewed` has the row and not the leader.
         */
        if (kept.unreviewed) {
            return kept.scenarioName === v.name ? kept : { ...kept, scenarioName: v.name };
        }
        /*
         * DECIDED, AND THE WORDS HAVE MOVED SINCE — back to undecided, with the new words
         * recorded, so it asks to be looked at exactly once per rename. A row with no recorded
         * name predates this and makes no claim, so it is left alone.
         */
        if (kept.scenarioName === undefined || kept.scenarioName === v.name)
            return kept;
        return { ...kept, unreviewed: true, scenarioName: v.name };
    });
    const live = new Set(versions.map(v => v.id));
    return { branches, removed: have.filter(b => !live.has(b.scenarioId)) };
}
/**
 * HOW MANY ROWS ARE STILL COPIES NOBODY HAS DECIDED — §4.4.
 *
 * Each block needs this twice: once inside the answer-setup box, where the strip only ever
 * speaks for the row you are on, and once in the block's issue list, which is what a teacher
 * who never pages to scenario 5 will actually see. Two hand-written filters per block, across
 * eleven blocks, is twenty-two chances for the box and the issue list to disagree about whether
 * the work is finished — and they already did on Numerical, which had the box and no issue.
 */
export function unreviewedCount(
/*
 * NOT `ScenarioBranchBase`, since a driven VERSION carries the same mark for the same reason
 * and asks the same question. Widened when the driven axis needed it, and renamed in the same
 * breath: `unreviewedCount` counting versions would have been a name that lies, and
 * the invariant pointing every block at it would have been pointing them at the lie.
 */
rows) {
    return (rows ?? []).filter(b => b.unreviewed).length;
}
/** Clear the duplicated-not-decided mark on one row, leaving every other row alone. Called by a
 *  block whenever that row's answer setup is edited. */
export function markScenarioReviewed(branches, scenarioId) {
    return branches.map(b => (b.scenarioId === scenarioId && b.unreviewed ? { ...b, unreviewed: undefined } : b));
}
/**
 * A display block's own content, keyed by what varies.
 *
 * The analogue of `fieldVersionKey`, and deliberately simpler: these blocks have no head, so
 * there is no `question` and no `feedback` to carry. `applyFieldVersion`'s own comment explains
 * why inventing either is harmful — a block gaining keys its editor never had is exactly what
 * `useRestoredData`'s field-set guard refuses.
 */
function displayKeys(data, keys) {
    return Object.fromEntries(keys.filter(k => k in data).map(k => [k, data[k]]));
}
/** Project a variant onto the block's own content fields — what the editor and the student
 *  renderer both read. A block with no bank is returned untouched, which is what makes this safe
 *  to call unconditionally. */
export function applyDisplayVersion(data, versionId, keys) {
    const version = pickVersion(data.versions, versionId);
    if (!version)
        return data;
    const out = { ...data };
    const rec = version;
    // Only what the variant actually carries: a key added to the list after a variant was authored
    // leaves that variant alone rather than blanking the field.
    for (const k of keys)
        if (k in rec)
            out[k] = rec[k];
    return out;
}
/** An edit lands in the VARIANT, and the block's own content fields keep what they had — they are
 *  a projection target that `applyDisplayVersion` overwrites, never a second copy to keep in step. */
export function writeDisplayVersion(current, versionId, next, keys) {
    const version = pickVersion(current.versions, versionId);
    if (!version)
        return next;
    const nextRec = next;
    const currentRec = current;
    const edited = { ...version, ...displayKeys(nextRec, keys) };
    const out = { ...nextRec, ...displayKeys(currentRec, keys) };
    out.versions = (current.versions ?? []).map(v => (v.id === version.id ? edited : v));
    return out;
}
/**
 * Add a variant, copied from the one in hand.
 *
 * A COPY rather than a blank, matching `addVersion`: a second scenario is nearly always a small
 * edit of the first, and starting from empty makes the teacher retype what they just wrote. The
 * FIRST call also captures the block's current content as variant one, so a block that has never
 * had a bank does not lose what is on screen the moment it gains one.
 */
export function addDisplayVersion(data, fromVersionId, newId, keys, 
/**
 * The name a new variant is born with, by ordinal — "Scenario 1", "Scenario 2".
 *
 * A DEFAULT, not a placeholder. A variant has to be referrable the moment it exists: a
 * follower's row names it, the leader's own pager names it, and those two must say the same
 * thing. Ghost text cannot do that, because nothing is stored for the follower to read. Same
 * convention as a new Special Event, which is auto-titled `rules.length + 1` and then freely
 * renamed; like those, it is not uniqueness-enforced and is never renumbered on a delete.
 *
 * Supplied by the caller because this package has no i18n, and the name is teacher-facing.
 */
nameFor) {
    const rec = data;
    const existing = data.versions ?? [];
    const base = existing.length === 0
        ? [{ id: newId(), name: nameFor?.(1), ...displayKeys(rec, keys) }]
        : existing;
    const from = pickVersion(base, fromVersionId) ?? base[0];
    // The copy takes the content and a name of its OWN: inheriting the source's would hand the
    // teacher two variants both called "Scenario 1", which is exactly the ambiguity a stored name
    // exists to remove.
    const copy = { ...from, id: newId(), name: nameFor?.(base.length + 1) };
    const at = base.findIndex(v => v.id === from.id);
    // The new id is RETURNED rather than left for the caller to spot: the first call mints two
    // (variant one, captured from the block, and the copy), so "whichever id is new" is ambiguous
    // exactly when the editor most needs to point at the one it just made.
    return {
        data: { ...data, versions: [...base.slice(0, at + 1), copy, ...base.slice(at + 1)] },
        addedId: copy.id,
    };
}
/**
 * Remove a variant.
 *
 * Dropping to ONE variant takes the bank away entirely and leaves that variant's content on the
 * block, because a one-variant bank is not one: `rollVersions` never draws from it and
 * `canLeadScenarios` will not offer it. Leaving the husk behind would mean a block that looks
 * banked, cannot lead, and silently never varies.
 */
export function removeDisplayVersion(data, versionId, keys) {
    const left = (data.versions ?? []).filter(v => v.id !== versionId);
    if (left.length === 0)
        return data;
    if (left.length === 1) {
        const only = applyDisplayVersion({ ...data, versions: left }, left[0].id, keys);
        return { ...only, versions: undefined };
    }
    return applyDisplayVersion({ ...data, versions: left }, left[0].id, keys);
}
/**
 * The roles a scenario row contributes once its OWN branching has run.
 *
 * `null` means fail closed — a row that branches and matches nothing has no answer key, and
 * handing back the row's unbranched `roles` there would mark a student against a comparison
 * the task never actually made.
 */
export function rolesForScenarioRow(row, resolvedVariables, angleMode) {
    const nested = row.conditionalBranches;
    if (!nested || nested.length === 0)
        return row.roles;
    const matched = resolveMatchedBranch(row.conditionalEquation, nested, resolvedVariables, angleMode);
    return matched ? matched.roles : null;
}
/**
 * The feedback a scenario row speaks with — innermost first.
 *
 * A nested branch may carry its own, which is the point of nesting at all: "the river is
 * faster, so you drift left" is not the sentence the other branch of the same scenario wants.
 * Falls back to the row's, then to the block's, which is `activeFeedbackForBranch`'s own ladder
 * one level deeper.
 */
export function feedbackForScenarioRow(row, resolvedVariables, angleMode) {
    const nested = row.conditionalBranches;
    if (!nested || nested.length === 0)
        return row.feedback;
    const matched = resolveMatchedBranch(row.conditionalEquation, nested, resolvedVariables, angleMode);
    // No match is a gap: the row's own feedback still answers, with targeted states stripped by
    // `activeFeedbackForBranch` upstream, so nothing fires terminally against an absent key.
    return matched?.feedback ?? row.feedback;
}
/**
 * The answer key for the scenario in force — `resolveScenarioOptions` for a family whose key is
 * a set of fields rather than a roles map.
 *
 * `noMatch` is the caller's, because "accepts nothing" is spelled differently per block: a
 * choice block sets every option neutral, a numerical one empties its accepted values. What is
 * shared is the RULE — a block that cannot identify its scenario must not grade against a key
 * it cannot justify, which is the same fail-closed `pickScenarioBranch` exists for.
 */
export function applyScenarioFields(data, branches, scenarioId, keys, noMatch) {
    if (!branches || branches.length === 0)
        return data;
    const matched = pickScenarioBranch(branches, scenarioId);
    if (!matched)
        return { ...data, ...noMatch };
    const out = { ...data };
    // Only what the row actually carries, exactly as `applyFieldVersion` does: a key added to the
    // list after a row was authored must leave that row alone rather than blanking the field.
    for (const k of keys)
        if (k in matched)
            out[k] = matched[k];
    return out;
}
/**
 * THE ROW THE EDITOR IS AUTHORING, merged into the block's shape.
 *
 * The read half of the value-range editor, and deliberately NOT `applyScenarioFields`: that one
 * fails closed, which is right for grading and wrong here. A teacher paging to a row is asking
 * to see it, and the pointer only ever names a row that exists — so an unmatched id here means
 * a dangling leader, where showing the block's own fields is more use than showing nothing.
 */
export function applyScenarioRow(data, scenarioId, keys) {
    const branches = data.scenarioBranches;
    const matched = pickScenarioBranch(branches, scenarioId);
    if (!matched)
        return data;
    const out = { ...data };
    for (const k of keys)
        if (k in matched)
            out[k] = matched[k];
    return out;
}
/**
 * THE WRITE HALF — an edit made while a row is showing belongs to that row.
 *
 * Exactly `writeFieldVersion`'s bargain, one selector over: the keys that make up the answer
 * key go into the row, and everything else — the units, the format gates, the question — stays
 * on the block, because those are shared across every scenario.
 *
 * The block's OWN copies of the row keys are deliberately left untouched once rows exist. They
 * are vestigial from that point, frozen at whatever they held when the link was made, and
 * writing the edited copy into them too would make a block that looks correct in two places
 * and can disagree with itself.
 *
 * Identity when there is no row, exactly as the version writer is: `next` straight back.
 */
export function writeScenarioRow(current, scenarioId, next, keys) {
    const branches = current.scenarioBranches;
    const matched = pickScenarioBranch(branches, scenarioId);
    if (!matched || !branches)
        return next;
    const rowFields = Object.fromEntries(keys.filter(k => k in next).map(k => [k, next[k]]));
    const out = { ...next };
    // The row keys revert to what the BLOCK holds; only the row records the edit.
    for (const k of keys)
        if (k in current)
            out[k] = current[k];
    out.scenarioBranches = branches.map(b => (b.scenarioId === matched.scenarioId ? { ...b, ...rowFields } : b));
    return out;
}
/**
 * TURN AN EXISTING QUESTION BANK INTO SCENARIO ROWS — §7.1.
 *
 * The case this exists for is the one the whole feature exists for: the owner's exam already
 * encodes its scenarios as per-block versions, because versions were the only tool there was.
 * Refusing to link such a block would make the migration a rebuild.
 *
 * ## It pairs by POSITION, and says so
 *
 * Version *i* becomes the row for variant *i*. Nothing anywhere claims version 2 was scenario
 * 2 — the pairing is a GUESS, and that is exactly why every remapped row comes back
 * `unreviewed`. The flag is not decoration here; it is the whole reason a remap is safe to
 * offer. It does not assert the mapping is right, it saves the typing and asks the teacher to
 * check, which is faster than rebuilding and is what they would otherwise do by hand.
 *
 * ## What travels, and what does not
 *
 * The answer key and the feedback move across; both are already authored. The version's
 * QUESTION is dropped, because it held the scenario prose and that is now the leader's job —
 * the block keeps whichever question it is showing, and the teacher trims the scenario out of
 * it.
 *
 * ## A count mismatch is reported, never absorbed
 *
 * Fewer versions than variants leaves the surplus rows built from the block's own setup and
 * unreviewed, which is harmless. MORE versions than variants means answer keys with nowhere to
 * go, so `surplus` says how many and the caller must tell the teacher before anything is
 * written. Silently dropping them would be §5.2's truncation again.
 */
export function remapVersionsToScenarios(leaderVersions, versions, 
/** The row payload carried over from version *i*. */
fromVersion, 
/** The row payload for a variant no version reached — the same copy linking would make. */
fromBlock) {
    const variants = leaderVersions ?? [];
    const banked = versions ?? [];
    const branches = variants.map((v, i) => {
        const from = banked[i];
        return {
            ...(from ? fromVersion(from) : fromBlock()),
            scenarioId: v.id,
            // EVERY row, including the ones built from the block's own setup: a position nobody
            // authored is no more decided than a guess at which version matched which scenario.
            unreviewed: true,
        };
    });
    return { branches, surplus: Math.max(0, banked.length - variants.length) };
}
/**
 * A VALUE BLOCK'S whole answer setup for the scenario in force — fields and feedback together.
 *
 * The value family (Numerical, Slider, Compass, Tilt, Rotation) differs from the choice family
 * in where its answer lives: not a roles map over options that already exist, but a set of
 * FIELDS — the condition type, the answers, the tolerances. So a row carries those fields flat,
 * keyed exactly as that block's `*_VERSION_KEYS` names them, and the same projection serves a
 * version and a scenario.
 *
 * Numerical had this written out and four blocks were about to copy it. What varies between
 * them is only the key list and what "no answer at all" looks like; what must NOT vary is the
 * pair of rules around it:
 *
 *   - OUTSIDE GRADED, or unlinked, the block is returned untouched — safe to call always.
 *   - A linked block whose scenario has NO ROW gets `failClosed` for its fields and keeps the
 *     block's own feedback. Empty answer lists mean nothing can be correct, which is the same
 *     fail-closed choice the choice family makes by going all-neutral.
 */
export function resolveScenarioValueSetup(d, scenarioId, keys, 
/** What this block's answer fields look like when there is no answer — usually empty lists. */
failClosed) {
    const branches = d.scenarioBranches;
    if (d.gradingMode !== 'graded' || !branches?.length)
        return d;
    const withFields = applyScenarioFields(d, branches, scenarioId, keys, failClosed);
    const row = pickScenarioBranch(branches, scenarioId);
    // The row's feedback, or the block's — `activeFeedbackForBranch`'s rule, applied where the
    // whole setup is assembled rather than at each evaluator.
    return row ? { ...withFields, feedback: row.feedback } : withFields;
}
export function scenarioInForce(leaderVersions, drawnId) {
    return pickVersion(leaderVersions, drawnId);
}
/**
 * A follower's effective options for the scenario in force.
 *
 * Mirrors `resolveEffectiveOptions`, including the part that matters most: an unlinked block, or
 * one with no rows, is returned UNCHANGED, so this is safe to call unconditionally.
 *
 * A linked block whose scenario has no row goes ALL NEUTRAL, which is the same fail-closed
 * choice the numeric branches make and for the same stated reason — failing loudly (impossible
 * to answer correctly) beats failing open (a free pass nobody notices for years). By §4.4's
 * projection every scenario has a row, so reaching this means something is already wrong, and
 * marking a student against the block's own unscoped answer key would be the quiet wrong answer.
 */
export function resolveScenarioOptions(options, branches, scenarioId, 
/*
 * REQUIRED, like `resolveScenarioRoles`'s own — and these two were optional here for exactly
 * one release, which was long enough for MCQ's editor-side chip derivation to omit them while
 * `evaluateMCQ` passed them. The canvas card then showed "Unreachable" for a row whose nested
 * branch had a perfectly good correct answer: the teacher saw one answer key and the student
 * was graded against another, the one failure the shared derivation exists to prevent.
 * A default is a wrong answer the compiler supplies on your behalf.
 */
resolvedVariables, angleMode) {
    return resolveScenarioRoles(options, branches, scenarioId, os => os.map(o => ({ ...o, role: 'neutral' })), 
    // The SAME merge a version applies — an option the key does not mention keeps its own role
    // and its own identity, so a row authored before an option was added leaves it alone.
    (os, roles) => versionRoles(os, roles), resolvedVariables, angleMode);
}
/**
 * The same resolution for a block whose roles do not live on a flat list keyed by `id`.
 *
 * THREE SHAPES, ONE RULE. Dropdown, MCQ, Keyword and GPS carry roles on options keyed by id;
 * FITB carries them on WORDS nested inside blanks; Scale carries them on values keyed by a
 * trigger derived from the number. Each already has its own `resolveEffective*` doing the
 * branch version of this, with its own merge — so what has to be shared is not the merge but
 * the rule ABOVE it, which is the part that is easy to get subtly wrong:
 *
 *   - an unlinked block, or one with no rows, is returned UNCHANGED (safe to call always)
 *   - a linked block whose scenario has NO ROW goes ALL NEUTRAL, never back to its own key
 *
 * That second line is the fail-closed choice, and writing it out once per block is nine
 * chances to write `return items` instead — which fails OPEN, marks a student against a
 * scenario they were never shown, and looks exactly like working code.
 */
export function resolveScenarioRoles(items, branches, scenarioId, 
/** Everything neutral, in this block's own shape. */
neutralise, 
/** This block's own role merge — the one its `resolveEffective*` already uses. */
merge, 
/**
 * For a row that branches on a comparison of its own — see `rolesForScenarioRow`.
 *
 * REQUIRED, not optional, and deliberately so: a block that forgot to pass them would
 * resolve the scenario and silently ignore the row's branching, which grades against the
 * row's unbranched key and looks exactly like working code. An empty record is the honest
 * way to say "this block has no variables", and nested branching then never matches.
 */
resolvedVariables, angleMode) {
    if (!branches || branches.length === 0)
        return items;
    const matched = pickScenarioBranch(branches, scenarioId);
    if (!matched)
        return neutralise(items);
    const roles = rolesForScenarioRow(matched, resolvedVariables, angleMode);
    // A row that branches and matches nothing is a gap, and gaps fail closed at every level.
    if (!roles)
        return neutralise(items);
    return merge(items, roles);
}
// ── Collapsing a set back into one block ─────────────────────────────────────
/**
 * How Scale names a point in a `roles` map.
 *
 * Lives here, below Scale, because both Scale and the collapse below have to agree on it and
 * `scale.ts` already imports from this module — so this is the direction that does not make a
 * cycle. A second copy of the string in either file is the drift this avoids.
 */
export const SCALE_VALUE_TRIGGER_PREFIX = 'value_';
/**
 * A STORED BLOCK'S TOLERANCE FIELDS UNDER THEIR CURRENT NAMES.
 *
 * `errorValue` / `errorAbove` / `errorBelow` / `errorMode` / `errorSymmetry` were renamed to
 * `tolerance*` because that is what they always meant, and because `error` means a FAULT
 * everywhere else here — `unit_error`, `notation_error`, `sigfig_error`. The rename moved the
 * code; it cannot move the block documents already saved.
 *
 * So every parse runs its raw input through this first. Without it a saved tolerance reads back
 * `undefined`, `?? ''` turns that into an empty tolerance, and an empty tolerance grades EXACT —
 * every band a teacher ever authored would quietly become a point, with nothing on screen saying
 * so. That is the failure mode this exists for: not a crash, a silent narrowing.
 *
 * One way. Writes go out under the new names only, as the `unitForms` migration already does, so
 * a block heals the first time it is saved.
 *
 * The new name WINS where both are present, which is what makes it safe to run on data that has
 * already been migrated.
 */
const LEGACY_TOLERANCE_NAMES = {
    errorValue: 'toleranceValue',
    errorAbove: 'toleranceAbove',
    errorBelow: 'toleranceBelow',
    errorMode: 'toleranceMode',
    errorSymmetry: 'toleranceSymmetry',
    handleBErrorMode: 'handleBToleranceMode',
    handleBErrorSymmetry: 'handleBToleranceSymmetry',
};
function renameLegacyKeys(o) {
    if (!o || typeof o !== 'object' || Array.isArray(o))
        return o;
    const src = o;
    let out = null;
    for (const [legacy, current] of Object.entries(LEGACY_TOLERANCE_NAMES)) {
        if (src[legacy] !== undefined && src[current] === undefined) {
            out ??= { ...src };
            out[current] = src[legacy];
        }
    }
    return out ?? src;
}
export function withLegacyToleranceNames(raw) {
    const top = renameLegacyKeys(raw);
    let out = top === raw ? null : top;
    for (const key of ['answers', 'betweenAnswers']) {
        const list = top[key];
        if (!Array.isArray(list))
            continue;
        const mapped = list.map(renameLegacyKeys);
        if (mapped.some((v, i) => v !== list[i])) {
            out ??= { ...top };
            out[key] = mapped;
        }
    }
    return out ?? top;
}
/**
 * Which keys each FIELD-based follower keeps in its rows, so a collapse can put them back.
 *
 * Declared here rather than imported from the four block modules because this file must not
 * depend on them — `scenario.ts` is what they import. The lists are the same ones
 * `*_VERSION_KEYS` declare, and `scenarioCollapseKeysAgree` in the block tests holds them equal.
 */
const COLLAPSE_FIELD_KEYS = {
    numerical: ['conditionType', 'answers', 'toleranceMode', 'toleranceSymmetry', 'betweenAnswers', 'threshold'],
    slider: ['conditionType', 'answers', 'toleranceMode', 'toleranceSymmetry', 'betweenAnswers', 'threshold', 'handleB'],
    compass: ['conditionType', 'answers', 'toleranceSymmetry', 'betweenAnswers'],
    tilt: ['conditionType', 'answers', 'toleranceSymmetry', 'betweenAnswers', 'threshold'],
    gps: ['targetLat', 'targetLng'],
};
/** Write a `Record<id, role>` back over a collection that carries `role` per member. */
function applyRoles(items, roles, keyOf) {
    if (!items || !roles)
        return items;
    // Only where the row has something to say. A member added to the block AFTER the link has no
    // entry in the row, and defaulting it to anything would be inventing an answer.
    return items.map((it, i) => (keyOf(it, i) in roles ? { ...it, role: roles[keyOf(it, i)] } : it));
}
/**
 * COLLAPSE A FOLLOWER BACK INTO A PLAIN BLOCK, keeping the surviving scenario's answer.
 *
 * Called when a leader's bank falls below two variants and can no longer lead: the followers
 * have to stop following, and the question is what answer they are left holding.
 *
 * NOT the one they had before they linked. A block's own answer keys are frozen at the moment
 * of linking and go stale from there — `writeScenarioRow` says so in as many words, because an
 * edit made while a row is showing belongs to that row and deliberately does not write through.
 * So reverting to them silently discards every answer authored since, and replaces it with one
 * that may be months old. The row that SURVIVED is the answer to the question that survived, so
 * that is what the block keeps.
 *
 * `survivorId` NAMES the scenario to keep, and the caller has to supply it, because the data
 * cannot. Removing the second-to-last variant collapses the bank entirely — `removeDisplayVersion`
 * absorbs the survivor into the block and clears `versions` — so from the leader's data alone
 * both variants look deleted and "the one still standing" is unknowable. Falls back to the first
 * row when unnamed, which is right for the paths that collapse without a deletion.
 *
 * Returns the same object when there is nothing to do, so a caller can test identity.
 */
export function collapseScenarioFollower(type, data, survivorId) {
    if (!data.followsBlockId)
        return data;
    const rows = data.scenarioBranches;
    const row = (survivorId ? rows?.find(r => r.scenarioId === survivorId) : undefined) ?? rows?.[0];
    // Unlinking is the part that always happens. A follower with no rows at all — linked but never
    // projected — still has to stop pointing at a block that no longer leads.
    const { followsBlockId: _gone, scenarioBranches: _rows, ...rest } = data;
    let out = rest;
    if (!row)
        return out;
    const fieldKeys = COLLAPSE_FIELD_KEYS[type];
    if (fieldKeys) {
        for (const k of fieldKeys)
            if (k in row)
                out[k] = row[k];
    }
    else if (type === 'ordering') {
        // The row holds the ORDER as a list of ids; the block holds the items themselves. Items the
        // row does not name keep their place at the end, so an item added after the link survives.
        const order = row.order;
        const items = out.items;
        if (order && items) {
            const byId = new Map(items.map(i => [i.id, i]));
            const named = order.map(id => byId.get(id)).filter((i) => !!i);
            out.items = [...named, ...items.filter(i => !order.includes(i.id))];
        }
    }
    else if (type === 'fitb') {
        const roles = row.roles;
        const blanks = out.blanks;
        if (roles && blanks) {
            out.blanks = blanks.map(b => ({ ...b, words: applyRoles(b.words, roles, w => w.id) }));
        }
    }
    else if (type === 'scale') {
        // Scale keys its roles by VALUE, not by an id — a point on the axis is identified by where
        // it sits, so inserting a point does not renumber the others' answers.
        out.values = applyRoles(out.values, row.roles, v => `${SCALE_VALUE_TRIGGER_PREFIX}${v.value}`);
    }
    else {
        // mcq, dropdown, keyword — one flat `options` list keyed by option id.
        out.options = applyRoles(out.options, row.roles, o => o.id);
    }
    // The row's feedback goes with its answer. They were authored together and a correct answer
    // paired with another scenario's explanation is worse than either alone.
    if (row.feedback !== undefined)
        out.feedback = row.feedback;
    return out;
}
//# sourceMappingURL=scenario.js.map